import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import { RpiTableColumns } from "../../rpi-core/table/RpiTable";
import { createEntityColumn } from "../../rpi-core/table/RpiTableHelpers";
import { CellRoute } from "../../components/cells/CellLink";
import { CellOrganization } from "../../components/cells/CellOrganization";
import { formatCurrencyV2 } from "../../utils/misc.util";
import { RpiTextField } from "../../components/RpiTextField";
import { Body } from "../../components/Typography";

const customerProductColumn = createEntityColumn<
  GraphQLPagedQueryData<GraphQL.CustomerProductsQuery | GraphQL.CustomerProductsSuperAdminQuery>,
  GraphQL.CustomerProductSearchField
>();
const baseCustomerProductColumnDefs = {
  name: customerProductColumn<"name" | "id">({
    header: "Product Name",
    accessor: "name",
    cell: ({ row }) => <CellRoute route={["customer-product", { id: row.id }]} children={row.name} />,
    size: 160,
  }),
  customerSku: customerProductColumn<"customerSku">({
    header: "Product SKU",
    accessor: "customerSku",
    size: 315,

    cell: ({ row }) => (
      <RpiTextField copyable inheritStyles>
        <Body size="sm">{row.customerSku}</Body>
      </RpiTextField>
    ),
  }),
  openapiSku: customerProductColumn<"openapiProduct">({
    header: "Manufacturing SKU",
    tooltip: "This is the SKU used by the printing manufacturer",
    size: 170,
    cell: ({ row }) => (
      <RpiTextField copyable inheritStyles>
        <Body size="sm">{row.openapiProduct?.openapiSku || "SKU LOOKUP ERROR"}</Body>
      </RpiTextField>
    ),
  }),
  basePrice: customerProductColumn<"openapiProduct">({
    header: "Base Price",
    accessor: "openapiProduct",
    tooltip: "Base price is a unit price of first 20 pages.",
    cell: ({ row }) => {
      const pricing = row.openapiProduct?.productPricing?.[0];
      return pricing ? formatCurrencyV2(pricing.currency, pricing.basePrice) : "-";
    },
    maxSize: 110,
  }),
  incrementalPrice: customerProductColumn<"openapiProduct">({
    header: "+ Cost",
    accessor: "openapiProduct",
    tooltip: "Additional cost per page that exceeds the first 20 pages.",
    cell: ({ row }) => {
      const pricing = row.openapiProduct?.productPricing?.[0];
      return pricing ? formatCurrencyV2(pricing.currency, pricing.perPageCost) : "-";
    },
    maxSize: 110,
  }),
};

const superAdminColumns = createEntityColumn<
  GraphQLPagedQueryData<GraphQL.CustomerProductsSuperAdminQuery>,
  GraphQL.CustomerProductSearchField
>();
const organizationName = superAdminColumns<"customer">({
  header: "Organization",
  accessor: "customer",
  cell: ({ row }) => <CellOrganization customer={row.customer} />,
  size: 160,
});

export const customerProductColumnsV2: RpiTableColumns<GraphQLPagedQueryData<GraphQL.CustomerProductsQuery>> = [
  baseCustomerProductColumnDefs.name,
  baseCustomerProductColumnDefs.customerSku,
  baseCustomerProductColumnDefs.openapiSku,
  baseCustomerProductColumnDefs.basePrice,
  baseCustomerProductColumnDefs.incrementalPrice,
];

export const customerProductColumnsSuperAdmin: RpiTableColumns<
  GraphQLPagedQueryData<GraphQL.CustomerProductsSuperAdminQuery>
> = [
  baseCustomerProductColumnDefs.name,
  organizationName,
  baseCustomerProductColumnDefs.customerSku,
  baseCustomerProductColumnDefs.openapiSku,
  baseCustomerProductColumnDefs.basePrice,
  baseCustomerProductColumnDefs.incrementalPrice,
];
